/* Users, memberships and invitations. Hand-typed to the pinned shapes in m6-rulings.md
   (#116, "Roles and users") until the access lane's schema.d.ts regenerates them. */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiSend } from "./client";
import type { Role } from "./types";

export type UserState = "active" | "suspended" | "invited";

/** `GET /api/roles` item; the four strings come from the server and are never restated here. */
export type RoleInfo = { key: Role; name: string; description: string; invitable: boolean };

/** One row of `GET /api/entities/{id}/users`: a membership or an open invitation. */
export type UserRow = {
  kind: "membership" | "invitation";
  membershipId: string | null;
  invitationId: string | null;
  userId: string | null;
  name: string | null;
  email: string;
  role: Role;
  state: UserState;
  lastSeenAt: string | null;
  expiresAt: string | null;
  invitedBy: string | null;
  isSelf: boolean;
};

export type AddUserRequest = { email: string; role: Role };
export type AddUserResponse =
  | { kind: "membership"; membershipId: string; state: "active" }
  | { kind: "invitation"; invitationId: string; state: "invited"; inviteUrl: string; expiresAt: string };

export type PatchMembershipRequest = { role?: Role; state?: "active" | "suspended" };

/** m6-rulings.md #132: the console scopes by entity; the portal's pinned company needs no id. */
export type UsersSource = "console" | "portal";
export type UsersScope = { source?: UsersSource | undefined; entityId?: string | undefined };

type UsersEndpoints = {
  list: string;
  membership: (membershipId: string) => string;
  invitation: (invitationId: string) => string;
};

function endpointsFor({ source = "console", entityId }: UsersScope): UsersEndpoints {
  if (source === "portal") {
    return {
      list: "/api/portal/users",
      membership: (membershipId) => `/api/portal/memberships/${membershipId}`,
      invitation: (invitationId) => `/api/portal/invitations/${invitationId}`,
    };
  }
  if (!entityId) throw new Error("users on the console need an entityId");
  return {
    list: `/api/entities/${entityId}/users`,
    membership: (membershipId) => `/api/memberships/${membershipId}`,
    invitation: (invitationId) => `/api/invitations/${invitationId}`,
  };
}

export const usersKey = ({ source = "console", entityId = "" }: UsersScope) =>
  ["users", source === "portal" ? "portal" : entityId] as const;

export function useRoles() {
  return useQuery({
    queryKey: ["roles"],
    queryFn: async () => (await apiGet<{ items: RoleInfo[] }>("/api/roles")).items,
    staleTime: Infinity,
  });
}

/** Not queue data: no polling; mutations invalidate the key. */
export function useEntityUsers(scope: UsersScope) {
  const at = endpointsFor(scope);
  return useQuery({
    queryKey: usersKey(scope),
    queryFn: async () => (await apiGet<{ items: UserRow[] }>(at.list)).items,
  });
}

/** `POST .../users` — the one "add a person" call; re-posting an open invitation reissues it. */
export function useAddUser(scope: UsersScope) {
  const at = endpointsFor(scope);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AddUserRequest) => apiSend<AddUserResponse>("POST", at.list, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: usersKey(scope) }),
  });
}

/** `PATCH .../memberships/{mid}` — optimistic; a refusal rolls the row back. */
export function usePatchMembership(scope: UsersScope) {
  const at = endpointsFor(scope);
  const queryClient = useQueryClient();
  const key = usersKey(scope);
  return useMutation({
    mutationFn: ({ membershipId, ...body }: PatchMembershipRequest & { membershipId: string }) =>
      apiSend<UserRow>("PATCH", at.membership(membershipId), body),
    onMutate: async ({ membershipId, ...body }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<UserRow[]>(key);
      queryClient.setQueryData<UserRow[]>(key, (rows) =>
        rows?.map((row) => (row.membershipId === membershipId ? { ...row, ...body } : row)),
      );
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });
}

/** `DELETE .../invitations/{iid}` — 204. */
export function useRevokeInvitation(scope: UsersScope) {
  const at = endpointsFor(scope);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) => apiSend<void>("DELETE", at.invitation(invitationId)),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: usersKey(scope) }),
  });
}
